import * as React from 'react'
import { t } from 'ttag'
import { polyfill } from 'seamless-scroll-polyfill'
import {
  CompanyCountriesDto,
  CompanyId,
  DivisionCategoryRow,
  DivisionDto,
  DivisionId,
  GoalId,
  GoalWithProgress,
  GroupView,
  ProgressType,
  Role,
  UserCompanyDto,
  UserProfileDto,
  UserDailyDiaryProgress,
  UserCustomDailyProgress,
  UserDailyProgress,
} from '../../services/api.types'
import { ActivityFilter, ActivityView } from '../../views/Activity/store/ActivityReducer'
import {
  buildDivisionCategoryLeaderboardId,
  getDivisionCategoryIdsFromLeaderboards,
  isCollectiveType,
  isBonusType,
  isForceCollectiveTotal,
  getGroupSumFromActivityView,
  isDivisionCategoryLeaderboardId,
} from '../../common/helpers/challenges'
import YumLeaderboardTableReactQueryProvider from '../YumLeaderboardTable/YumLeaderboardTable'
import YumLegacyGoalRankingTableWrapper from './YumLegacyGoalRankingTableWrapper'
import { YumLegacyGoalRankingTableHandle } from './YumLegacyGoalRankingTable'
import YumGoalRankingHeaderWrapper from './YumGoalRankingHeader/YumGoalRankingHeaderWrapper'
import { useState } from 'react'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'
import YumGoalRankingTabs from './YumGoalRankingTabs'
import YumGoalRankingTabsCompact from './YumGoalRankingTabsCompact'
import YumChallengeGoalCompact from '../YumChallengeGoal/YumChallengeGoalCompact'
import YumChallengeCollectiveProgress from '../YumChallengeGoal/components/YumChallengeCollectiveProgress/YumChallengeCollectiveProgress'
import { Grid } from '@material-ui/core'
import { borderGray, primaryBlue } from '../../styles/material-ui-theme-v2'
import LeaderboardMembersModal from '../YumLeaderboardTable/LeaderboardMembersModal'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// @ts-ignore
polyfill()

export interface IStateProps {
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  goalDto: GoalWithProgress
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
  countries: CompanyCountriesDto[]
  divisions: DivisionDto[]
  divisionCategories: DivisionCategoryRow[]
  activityFilter: ActivityFilter
  activityView: ActivityView
  groupView: GroupView
  groupSum: boolean

  token?: string
  isLegacyRanking: boolean
  lastBonusPointsAdded: number | null
  dailyDiary: UserDailyDiaryProgress[]
  customProgress: UserCustomDailyProgress[]
  progressResults: UserDailyProgress[]
}

export interface IDispatchProps {
  setActivityView(payload?: any): void
  getCurrentGoalActivityList(payload?: {
    goalId: GoalId
    divisionId: DivisionId
    activityFilter?: ActivityFilter
    activityView?: ActivityView
    groupView?: GroupView
    groupSum?: boolean
    token?: string
  }): void
  getCurrentCompanyGoal({ id, token }: { id: GoalId; token?: string }): void
  getDivisionCategories: (payload?: { companyId: CompanyId }) => void
  getCompanyDivisions: (payload?: { companyId: CompanyId }) => void
}

type IActivityProps = IStateProps & IDispatchProps

interface State {
  defaultActivityFilter: ActivityFilter
  divisionId: DivisionId | null
  isSearchVisible: boolean
  searchValue: string
  isFindMeLoading: boolean
}

// Custom hook to manage activity data updates
const useActivityData = (props: IActivityProps, state: State) => {
  const {
    goalDto: {
      id: goalId,
      companyId,
      progressType,
      hidePeopleTab,
      hideGroupsTab,
      hideCountriesTab,
      goals,
      avgAgg,
      compactRankingView,
      leaderboards: leaderboardsRaw,
    },
    token,
    divisions: allDivisions,
    countries: allCountries,
    activityView: propsActivityView,
    groupView: propsGroupView,
    groupSum: propsGroupSum,
    getCurrentGoalActivityList,
  } = props

  const { divisionId, defaultActivityFilter } = state
  const activityFilter = props.activityFilter || defaultActivityFilter

  // Filter divisions by company
  const divisions = React.useMemo(
    () => (allDivisions || []).filter((d) => d.companyId === companyId),
    [allDivisions, companyId],
  )

  const noGroups = !divisions || divisions.length === 0
  const noCountries = !allCountries || allCountries.length === 0

  // Normalize leaderboards list from the goal payload.
  const leaderboards = React.useMemo(() => {
    let parsed: string[] = []
    if (!leaderboardsRaw) {
      parsed = []
    } else if (Array.isArray(leaderboardsRaw)) {
      parsed = leaderboardsRaw
    } else if (typeof leaderboardsRaw === 'string') {
      try {
        const decoded = JSON.parse(leaderboardsRaw)
        parsed = Array.isArray(decoded) ? decoded : []
      } catch (e) {
        parsed = []
      }
    }

    // New format includes ActivityView values or prefixed category IDs.
    const hasNewFormat = parsed.some(
      (leaderboardId) =>
        Object.values(ActivityView).includes(leaderboardId as ActivityView) ||
        isDivisionCategoryLeaderboardId(leaderboardId),
    )

    if (hasNewFormat) return parsed

    // Legacy format stored only raw category IDs.
    const legacyLeaderboards: string[] = [
      ...(!hidePeopleTab ? [ActivityView.INDIVIDUAL] : []),
      ...(!hideGroupsTab ? [ActivityView.GROUP] : []),
      ...parsed.map((categoryId) => buildDivisionCategoryLeaderboardId(categoryId)),
      ...(!hideCountriesTab ? [ActivityView.COUNTRY] : []),
    ]

    // Don't show for legacy:
    // if (isCollectiveType(progressType)) {
    //   legacyLeaderboards.push(ActivityView.COLLECTIVE)
    // }

    return legacyLeaderboards
  }, [leaderboardsRaw, hidePeopleTab, hideGroupsTab, hideCountriesTab, progressType])

  // Show collective tab only when it is enabled or for legacy collective challenges.
  const showCollectiveTab = React.useMemo(
    () => leaderboards.includes(ActivityView.COLLECTIVE) || isCollectiveType(progressType),
    [leaderboards, progressType],
  )

  // Track if any custom division category tabs are available.
  // This prevents forcing the view to Collective when custom group tabs exist.
  const hasDivisionCategoryLeaderboards = React.useMemo(
    () => leaderboards.some((leaderboardId) => isDivisionCategoryLeaderboardId(leaderboardId)),
    [leaderboards],
  )

  // Get activity view based on conditions
  const activityView = React.useMemo(() => {
    // If goal leaderboards are provided, start on the first one by default.
    // This keeps the initial tab aligned with the goal configuration.
    const firstLeaderboard = leaderboards.length > 0 ? (leaderboards[0] as ActivityView) : undefined
    let defaultView = propsActivityView || firstLeaderboard || ActivityView.INDIVIDUAL
    if (defaultView === ActivityView.INDIVIDUAL && hidePeopleTab) defaultView = ActivityView.GROUP
    if (defaultView === ActivityView.GROUP && (hideGroupsTab || noGroups)) defaultView = ActivityView.COUNTRY
    if (defaultView === ActivityView.COUNTRY && hideCountriesTab && !hidePeopleTab)
      defaultView = ActivityView.INDIVIDUAL
    if (defaultView === ActivityView.COUNTRY && hideCountriesTab && hidePeopleTab) defaultView = ActivityView.GROUP
    if (defaultView === ActivityView.COLLECTIVE && !showCollectiveTab) defaultView = ActivityView.INDIVIDUAL
    if (hidePeopleTab && hideGroupsTab && hideCountriesTab && showCollectiveTab && !hasDivisionCategoryLeaderboards) {
      defaultView = ActivityView.COLLECTIVE
    }
    return defaultView
  }, [
    propsActivityView,
    hidePeopleTab,
    hideGroupsTab,
    hideCountriesTab,
    noGroups,
    showCollectiveTab,
    hasDivisionCategoryLeaderboards,
    leaderboards,
  ])

  // Determine group view and sum
  const groupView =
    !allCountries || !allCountries.length ? GroupView.COUNTRIES_MERGED : propsGroupView || GroupView.COUNTRIES_MERGED

  // Determine group sum
  let groupSum = true
  if (compactRankingView && goals !== undefined && avgAgg !== undefined) {
    // Compact view: use the backend avgAgg value
    groupSum = !avgAgg
  } else {
    // Legacy view: use the user-toggled value from Redux
    const isCollectiveChallenge = isCollectiveType(progressType)
    groupSum = propsGroupSum ?? isCollectiveChallenge
  }

  // Update data function
  const updateData = React.useCallback(() => {
    getCurrentGoalActivityList({
      goalId,
      divisionId,
      activityFilter,
      activityView,
      groupView,
      groupSum,
      token,
    })
  }, [goalId, divisionId, activityFilter, activityView, groupView, groupSum, token, getCurrentGoalActivityList])

  return {
    divisions,
    activityView,
    groupView,
    groupSum,
    updateData,
    noGroups,
    noCountries,
    showCollectiveTab,
    hasDivisionCategoryLeaderboards,
    leaderboards,
  }
}

export const YumGoalRanking: React.FC<IActivityProps> = (props) => {
  const tableRef = React.useRef<YumLegacyGoalRankingTableHandle>(null)

  const [state, setState] = React.useState<State>({
    defaultActivityFilter: {
      countries: [],
      divisionIds: [],
    },
    divisionId: null,
    isSearchVisible: false,
    searchValue: '',
    isFindMeLoading: false,
  })

  const {
    goalDto, //: goalDtoRaw,
    userDto,
    userCompanies,
    activityFilter,
    hasRoleInCompany,
    isLegacyRanking,
    token,
    setActivityView,
    getCurrentCompanyGoal,
    lastBonusPointsAdded,
    divisionCategories,
    getDivisionCategories,
    getCompanyDivisions,
    dailyDiary,
    customProgress,
    progressResults,
  } = props

  // const goalDto = React.useMemo(() => {
  //   return {
  //     ...goalDtoRaw,
  //     splitRankingView: true,
  //   }
  // }, [goalDtoRaw])

  const compactLayout = goalDto.compactRankingView
  const hideLeaderboardTables = (goalDto as any).hideLeaderboardTables === true
  const showGroupMembers = (goalDto as any).showGroupMembers === true

  const { companyId, progressType, hidePeopleTab, hideGroupsTab, hideCountriesTab, participating } = goalDto
  const { userId } = userDto

  // Use custom hook for activity data management
  const {
    divisions,
    activityView,
    groupView,
    groupSum,
    updateData,
    noGroups,
    noCountries,
    showCollectiveTab,
    hasDivisionCategoryLeaderboards,
    leaderboards,
  } = useActivityData(props, state)

  // Update data on mount for legacy ranking
  React.useEffect(() => {
    if (isLegacyRanking) {
      updateData()
    }
  }, [isLegacyRanking]) // eslint-disable-line

  // Update divisionId when company changes
  React.useEffect(() => {
    const userCompany = userCompanies.find((uc) => uc.companyId === companyId)
    if (userCompany) {
      setState((prev) => ({ ...prev, divisionId: userCompany.divisionId }))
    }
  }, [companyId, userCompanies])

  // Load DivisionCategories and Divisions when company changes
  if (!token) {
    React.useEffect(() => {
      if (companyId) {
        getDivisionCategories({ companyId })
        getCompanyDivisions({ companyId })
      }
    }, [companyId, getDivisionCategories, getCompanyDivisions])
  }

  const handleTabChange = React.useCallback(
    (event: React.ChangeEvent<{}>, newActivityView: ActivityView) => {
      const { id: goalId } = goalDto

      // Clear search value and hide search when changing tabs
      setState((prev) => ({
        ...prev,
        searchValue: '',
        isSearchVisible: false,
      }))

      if (isLegacyRanking) {
        const { divisionId } = state
        const activityFilter = props.activityFilter || state.defaultActivityFilter
        props.getCurrentGoalActivityList({ goalId, divisionId, activityFilter, activityView: newActivityView, token })
      } else {
        setActivityView({ goalId, activityView: newActivityView })
      }
    },
    [goalDto, isLegacyRanking, state, props, token, setActivityView],
  )

  const [wasFindMeTriggered, setWasFindMeTriggered] = React.useState(false)

  const handleFindMe = React.useCallback(() => {
    // First, clear any existing search value
    setState((prev) => ({
      ...prev,
      searchValue: '',
    }))

    if (compactLayout) {
      setWasFindMeTriggered(true)
      setState((prev) => ({
        ...prev,
        isSearchVisible: false,
        isFindMeLoading: true,
      }))
    } else if (!isLegacyRanking) {
      setWasFindMeTriggered(true)
      setState((prev) => ({
        ...prev,
        isSearchVisible: true,
        isFindMeLoading: true,
      }))
      // Let the YumLeaderboardTable component know to find the user
      // The actual implementation of find me remains in the table component
      // We're just using this as a signal
    } else {
      tableRef.current?.findMe()
    }
  }, [isLegacyRanking, compactLayout])

  const setSearchValue = React.useCallback((value: string) => {
    setState((prev) => ({ ...prev, searchValue: value }))
  }, [])

  const setIsSearchVisible = React.useCallback((visible: boolean) => {
    setState((prev) => ({ ...prev, isSearchVisible: visible }))
  }, [])

  const setIsFindMeLoading = React.useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isFindMeLoading: loading }))
  }, [])

  // Reset find me loading state when search value changes or search is closed
  React.useEffect(() => {
    // If user is typing in search, clear the find me loading state
    if (state.searchValue || !state.isSearchVisible) {
      setState((prev) => ({ ...prev, isFindMeLoading: false }))
      setWasFindMeTriggered(false)
    }
  }, [state.searchValue, state.isSearchVisible])

  // Reset wasFindMeTriggered flag when loading state changes to false
  React.useEffect(() => {
    if (!state.isFindMeLoading && wasFindMeTriggered) {
      setWasFindMeTriggered(false)
    }
  }, [state.isFindMeLoading, wasFindMeTriggered])

  if (!Object.values(ProgressType).includes(progressType)) {
    return <div className="text-center">{t`Challenge type not supported. Please check for updates.`}</div>
  }

  const userCompany = userCompanies.find((uc) => uc.companyId === companyId) || { divisionId: '', country: '' }
  const { country } = userCompany

  // Detect if the user's team/group is excluded from the leaderboard (e.g. due to minimum team size).
  // Only set when a min-size threshold is actually configured — otherwise the back-end can't exclude
  // anyone, so there's no point doing the lookup or showing the banner.
  const excludedTeamInfo = React.useMemo(() => {
    const isGroupView = activityView === ActivityView.GROUP
    const isCategoryView = typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_')
    if (!isGroupView && !isCategoryView) return null

    const userDivisionId = state.divisionId || goalDto.divisionId
    if (!userDivisionId) return null

    if (isGroupView) {
      const minTeamSize = (userCompany as UserCompanyDto).minTeamSize ?? 0
      if (!minTeamSize || minTeamSize <= 0) return null
      const teamName = divisions?.find((d) => d.id === userDivisionId)?.name
      if (!teamName) return null
      return { teamId: userDivisionId as DivisionId, teamName }
    }

    if (isCategoryView) {
      const categoryId = activityView.replace('DIVISION_CATEGORY_', '')
      const minGroupSize = divisionCategories?.find((c) => c.id === categoryId)?.minSize ?? 0
      if (!minGroupSize || minGroupSize <= 0) return null
      let parsedRanks: any = null
      try {
        parsedRanks =
          typeof goalDto.divisionCategoryRanks === 'string'
            ? JSON.parse(goalDto.divisionCategoryRanks)
            : goalDto.divisionCategoryRanks
      } catch {
        // ignore
      }
      const catDivisionId = parsedRanks?.[categoryId]?.id as DivisionId
      if (!catDivisionId) return null
      const teamName = divisions?.find((d) => d.id === catDivisionId)?.name
      if (!teamName) return null
      return { teamId: catDivisionId, teamName }
    }

    return null
  }, [
    activityView,
    state.divisionId,
    goalDto.divisionId,
    goalDto.divisionCategoryRanks,
    divisions,
    divisionCategories,
    userCompany,
  ])

  const [selfNotFound, setSelfNotFound] = React.useState(false)

  // Reset when switching tabs so the message doesn't flash
  React.useEffect(() => {
    setSelfNotFound(false)
  }, [activityView])
  const [membersModalFromHeader, setMembersModalFromHeader] = React.useState(false)
  const [headerQueryClient] = React.useState(() => new QueryClient())

  const companyCountries = props.countries.find((c) => c.companyId === companyId) || { countries: [] }
  const { countries } = companyCountries

  // Show tabs logic
  const showTabs = React.useMemo(() => {
    // Always show tabs in compact layout
    if (compactLayout) {
      return true
    }
    const hasValidDate = goalDto.startDate >= 1585688400000
    const hasValidData = (divisions && divisions.length) || (countries && countries.length)
    const hasDivisionCategories =
      divisionCategories &&
      divisionCategories.length > 0 &&
      getDivisionCategoryIdsFromLeaderboards(leaderboards).length > 0
    const tooManyHiddenTabs = [hidePeopleTab, hideGroupsTab, hideCountriesTab].filter(Boolean).length >= 2
    return !!(hasValidDate && (hasValidData || hasDivisionCategories) && !tooManyHiddenTabs)
  }, [
    goalDto.startDate,
    divisions,
    countries,
    divisionCategories,
    leaderboards,
    hidePeopleTab,
    hideGroupsTab,
    hideCountriesTab,
    compactLayout,
  ])

  const companyHasCountries = countries && countries.length > 0

  const [totalCount, setTotalCount] = useState(goalDto.participantsCount || 0)
  const [totalTeamCount, setTotalTeamCount] = useState(goalDto.teamRankFrom || 0)
  const [totalCountryCount, setTotalCountryCount] = useState(goalDto.countryRankFrom || 0)

  // Calculate if "Find me" button should be shown
  const shouldShowFindMe = React.useMemo(() => {
    if (compactLayout) return false

    // Don't show if we're not participating
    if (!participating) return false

    // If we're not in individual view, don't show "Find me"
    if (activityView !== ActivityView.INDIVIDUAL) return false

    // Check division filter
    if (
      activityFilter?.divisionIds.length > 0 &&
      state.divisionId &&
      !activityFilter?.divisionIds.includes(state.divisionId)
    ) {
      return false
    }

    // Check country filter
    if (activityFilter?.countries.length > 0 && !activityFilter?.countries.includes(country)) {
      return false
    }

    return true
  }, [
    compactLayout,
    activityView,
    activityFilter?.divisionIds,
    activityFilter?.countries,
    state.divisionId,
    country,
    participating,
  ])

  // Check if bonus points feature is enabled and if the goal has any bonus activities
  const bonusPointsFeature = hasFeatureFlag(userCompanies, 'bonusPoints')

  // Hide team name on individual/collective leaderboard if challenge has no team leaderboard
  const hideTeamNameOnLeaderboard = hasFeatureFlag(userCompanies, 'hideTeamIfNoTeamLeaderboard') && hideGroupsTab // !leaderboards.includes(ActivityView.GROUP)

  // Hide country flag on individual/collective leaderboard if challenge has no country leaderboard
  const hideCountryOnLeaderboard =
    hasFeatureFlag(userCompanies, 'hideCountryIfNoCountryLeaderboard') && hideCountriesTab // !leaderboards.includes(ActivityView.COUNTRY)

  // Per-category: hide custom group filter sections for categories that don't have a leaderboard tab
  const hiddenCustomGroupCategoryIds = React.useMemo(() => {
    if (!hasFeatureFlag(userCompanies, 'hideCustomGroupIfNoCustomGroupLeaderboard')) return []
    if (!divisionCategories?.length) return []
    return divisionCategories
      .filter((cat) => !leaderboards.includes(buildDivisionCategoryLeaderboardId(cat.id)))
      .map((cat) => cat.id)
  }, [userCompanies, divisionCategories, leaderboards])

  // Check if the goal has any bonus activities by parsing pointsLogic
  const hasBonusActivities = React.useMemo(() => {
    if (!bonusPointsFeature) return false

    try {
      const pointsLogic = goalDto.pointsLogic ? JSON.parse(goalDto.pointsLogic) : []
      return pointsLogic.some((item: any) => isBonusType(item.category))
    } catch (error) {
      // If parsing fails, assume no bonus activities
      return false
    }
  }, [bonusPointsFeature, goalDto.pointsLogic])

  return (
    <>
      {compactLayout && (
        <>
          <YumGoalRankingTabsCompact
            showTabs={showTabs}
            activityView={activityView}
            onTabChange={handleTabChange}
            hidePeopleTab={hidePeopleTab}
            hideGroupsTab={hideGroupsTab}
            hideCountriesTab={hideCountriesTab}
            showCollectiveTab={showCollectiveTab}
            divisions={divisions}
            countries={countries}
            divisionCategories={divisionCategories}
            leaderboards={leaderboards}
          />
          <YumChallengeGoalCompact
            activityView={activityView}
            goal={goalDto}
            dailyDiary={dailyDiary}
            customProgress={customProgress}
            progressResults={progressResults}
            isLeaderboard={!!token}
            divisionCategories={divisionCategories}
          />
        </>
      )}
      {isForceCollectiveTotal(goalDto.id) && (
        <YumChallengeCollectiveProgress goal={goalDto} isLeaderboard={!!token} totalOnly={true} />
      )}

      {/*<Grid item>
        <div
          //className={classes.link}
          //onClick={toggleShowDetails}
          style={{
            color: primaryBlue,
            fontSize: '13px',
            cursor: 'pointer',
            textAlign: 'center',
            marginBottom: '10px',
          }}
        >
          {t`Show diary`}
           {!showDetails ? t`Show diary` : t`Hide details`}
        </div>
      </Grid>*/}

      <div
        style={
          compactLayout
            ? {
                padding: '10px 10px 0 10px',
                //paddingTop: !showTabs ? '10px' : 0,
                background: 'white',
                borderRadius: 8,
              }
            : {}
        }
      >
        <YumGoalRankingHeaderWrapper
          goalDto={goalDto}
          userDto={userDto}
          divisions={divisions}
          countries={countries}
          divisionCategories={divisionCategories}
          activityFilter={activityFilter}
          defaultActivityFilter={state.defaultActivityFilter}
          activityView={activityView}
          groupView={groupView}
          groupSum={groupSum}
          isLegacyRanking={isLegacyRanking}
          searchValue={state.searchValue}
          setSearchValue={setSearchValue}
          shouldShowFindMe={shouldShowFindMe}
          isFindMeLoading={state.isFindMeLoading}
          hasRoleInCompany={hasRoleInCompany}
          onFindMe={handleFindMe}
          token={token}
          totalCount={totalCount}
          totalTeamCount={totalTeamCount}
          totalCountryCount={totalCountryCount}
          showTabs={showTabs}
          isSearchVisible={state.isSearchVisible}
          setIsSearchVisible={setIsSearchVisible}
          compactLayout={compactLayout}
          hideTeamFilter={hideTeamNameOnLeaderboard}
          hideCountryFilter={hideCountryOnLeaderboard}
          hiddenCustomGroupCategoryIds={hiddenCustomGroupCategoryIds}
          hideSearchAndFilter={hideLeaderboardTables}
        />
        <div
          style={
            compactLayout
              ? {}
              : {
                  padding: isLegacyRanking ? '0 10px 10px' : '0 10px',
                  paddingTop: !showTabs ? '10px' : 0,
                  background: 'white',
                  borderRadius: 8,
                }
          }
        >
          {!compactLayout && (
            <YumGoalRankingTabs
              showTabs={showTabs}
              activityView={activityView}
              onTabChange={handleTabChange}
              isLegacyRanking={isLegacyRanking}
              hidePeopleTab={hidePeopleTab}
              hideGroupsTab={hideGroupsTab}
              hideCountriesTab={hideCountriesTab}
              showCollectiveTab={showCollectiveTab}
              divisions={divisions}
              countries={countries}
              divisionCategories={divisionCategories}
              leaderboards={leaderboards}
            />
          )}
          {compactLayout ? (
            /*hideLeaderboardTables ? (
              <div style={{ height: '1px' }} />
            ) : (*/
            <div
              style={{
                width: '100%',
                height: '1px',
                position: 'relative',
                backgroundColor: '#e3e3e8',
                margin: '8px 0',
              }}
            />
          ) : /*)*/
          null}
          {selfNotFound && excludedTeamInfo && (
            <>
              <div
                id="excluded-team-banner"
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#24284e80',
                  fontSize: '14px',
                }}
              >
                {activityView === ActivityView.GROUP
                  ? t`Your team is not shown due to minimum team size.`
                  : t`Your group is not shown due to minimum group size.`}
                <br />
                <span
                  onClick={() => setMembersModalFromHeader(true)}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: '#24284e',
                    marginTop: '4px',
                    display: 'inline-block',
                  }}
                >
                  {activityView === ActivityView.GROUP ? t`View your team` : t`View your group`}
                </span>
              </div>
              {compactLayout && (
                <div
                  style={{
                    width: '100%',
                    height: '1px',
                    position: 'relative',
                    backgroundColor: '#e3e3e8',
                  }}
                />
              )}
            </>
          )}
          {membersModalFromHeader && excludedTeamInfo && (
            <QueryClientProvider client={headerQueryClient}>
              <LeaderboardMembersModal
                open={membersModalFromHeader}
                onClose={() => setMembersModalFromHeader(false)}
                goalId={goalDto.id}
                goalDto={goalDto}
                teamId={excludedTeamInfo.teamId}
                teamName={excludedTeamInfo.teamName}
                token={token}
                showContributions={showGroupMembers && (goalDto as any).showGroupMembersContributions === true}
              />
            </QueryClientProvider>
          )}
          {hideLeaderboardTables ? (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: '#24284e80',
                minHeight: '98px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {t`Leaderboard rankings are hidden for this challenge.`}
            </div>
          ) : !isLegacyRanking ? (
            <YumLeaderboardTableReactQueryProvider
              goalDto={goalDto}
              activityFilter={activityFilter}
              countries={countries}
              activityView={activityView}
              userId={userId}
              groupView={groupView}
              groupSum={groupSum}
              divisionId={state.divisionId || ''}
              country={country}
              showTabs={showTabs}
              token={token}
              searchValue={state.searchValue}
              setTotalCount={setTotalCount}
              setTotalTeamCount={setTotalTeamCount}
              setTotalCountryCount={setTotalCountryCount}
              lastSync={userDto.lastSync}
              setIsFindMeLoading={setIsFindMeLoading}
              getCurrentCompanyGoal={getCurrentCompanyGoal}
              isFindMeLoading={state.isFindMeLoading}
              lastBonusPointsAdded={lastBonusPointsAdded}
              bonusPointsFeature={bonusPointsFeature && hasBonusActivities}
              hideTeamName={hideTeamNameOnLeaderboard}
              hideCountry={hideCountryOnLeaderboard}
              hasRoleInCompany={hasRoleInCompany}
              dailyDiary={dailyDiary}
              customProgress={customProgress}
              progressResults={progressResults}
              compactLayout={compactLayout}
              divisions={divisions}
              onSelfNotFound={setSelfNotFound}
              excludedTeamInfo={excludedTeamInfo}
            />
          ) : (
            <YumLegacyGoalRankingTableWrapper
              ref={tableRef}
              goalDto={goalDto}
              activityView={activityView}
              groupView={groupView}
              companyHasCountries={companyHasCountries}
              compactLayout={compactLayout}
            />
          )}
        </div>
      </div>
    </>
  )
}
